// packages/web/src/screens/RecordGrantPortal.tsx — Core hardening
// 2026-07-29, Item 9: the generic sibling to ReviewerPortal.tsx's own
// bespoke review flow. External, unauthenticated, reached by the hash
// route #/portal/:token (deliberately distinct from
// #/portal/review/:token's own namespace — this screen is the generic
// sibling, not a replacement). The token is the ONLY credential — every
// call is authorized solely by it, exactly like ReviewerPortal.
//
// Renders EITHER of the two grant target kinds the backend can resolve
// (packages/core/src/grants/portalTarget.ts's PortalTargetView):
//   - 'proposal_approval': a minimal summary + one "Approve" button (the
//     existing /p/:token/approve flow finally gets a UI here, at no extra
//     backend cost — nothing new needed server-side for this branch).
//   - 'record_view': the record rendered via FieldValueList (Item 5),
//     fully generic — zero HVAC or Actor-specific rendering logic — plus
//     the ONE action this grant allows (view/approve/sign/rate/upload).
import { useEffect, useState } from 'preact/hooks';
import {
  getRecordGrantPortal,
  submitRecordGrantAction,
  ApiError,
  type PortalTargetView,
  type GrantAllowedAction,
  type RecordRow,
} from '../lib/api';
import { PortalFrame } from '../components/PortalFrame';
import { FieldValueList } from '../components/FieldValueList';
import './RecordGrantPortal.css';

/** FieldValueList (Item 5) takes a full operator-facing RecordRow, but the
 * portal's own PortalRecordView (deliberately narrow — see
 * portalTarget.ts's own header) only carries {id, recordType, displayName,
 * fields}. Fills the rest with inert placeholders FieldValueList never
 * reads (it only touches `.fields` and `.id`) — a real, type-correct
 * RecordRow, not an unsafe cast. */
function toRecordRow(record: { id: string; recordType: string; displayName: string; fields: Record<string, unknown> }): RecordRow {
  return {
    id: record.id,
    tenant_id: '',
    record_type: record.recordType,
    display_name: record.displayName,
    fields: record.fields,
    lifecycle_state: null,
    sensitivity: 'normal',
    status: 'active',
    created_by: null,
    created_at: '',
    updated_at: '',
  };
}

export interface RecordGrantPortalProps {
  token: string;
  /** The DEPLOYMENT's product name, from the pack's entry config. `null`
   * while it is still being fetched — never a guessed name on a stranger's
   * screen. See PortalFrame's `brand`. */
  brand?: string | null;
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'view'; view: PortalTargetView }
  | { kind: 'done'; action: GrantAllowedAction };

const ACTION_LABEL: Record<GrantAllowedAction, string> = {
  view: '',
  approve: 'Approve',
  sign: 'Sign',
  rate: 'Rate',
  upload: 'Send',
};

/** The receipt, per action — what happened to what, in words that fit the
 * act. Composing it from the button label produced "Your approve is on its
 * way." */
const DONE_LINE: Record<GrantAllowedAction, string> = {
  view: 'Your response is on its way.',
  approve: 'Your approval is on its way.',
  sign: 'Signed — it’s on its way back.',
  rate: 'Your rating is on its way.',
  upload: 'Sent — it’s on its way.',
};

export function RecordGrantPortal({ token, brand }: RecordGrantPortalProps) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    getRecordGrantPortal(token)
      .then((view) => {
        if (cancelled) return;
        setPhase(view ? { kind: 'view', view } : { kind: 'invalid' });
      })
      .catch(() => {
        if (!cancelled) setPhase({ kind: 'invalid' });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (phase.kind === 'loading') {
    return (
      <PortalFrame brand={brand} footNote="A private link. No account needed.">
        <div class="portal__soft">One moment…</div>
      </PortalFrame>
    );
  }

  if (phase.kind === 'invalid') {
    return (
      <PortalFrame brand={brand} footNote="A private link. No account needed.">
        <div class="portal__lead">This link is no longer active.</div>
        <div class="portal__soft">It may have expired, been used, or been turned off. Ask for a fresh link.</div>
      </PortalFrame>
    );
  }

  if (phase.kind === 'done') {
    return (
      <PortalFrame brand={brand} footNote="A private link. No account needed.">
        <div class="portal__lead">Done — thank you.</div>
        <div class="portal__soft">{DONE_LINE[phase.action]}</div>
      </PortalFrame>
    );
  }

  return (
    <RecordGrantPortalView
      brand={brand}
      token={token}
      view={phase.view}
      onDone={(action) => setPhase({ kind: 'done', action })}
      onGone={() => setPhase({ kind: 'invalid' })}
    />
  );
}

interface RecordGrantPortalViewProps {
  /** The deployment's product name — see PortalFrame's `brand`. */
  brand?: string | null;
  token: string;
  view: PortalTargetView;
  onDone: (action: GrantAllowedAction) => void;
  /** The link died under us — consumed or invalid at submit time. The screen
   * moves to the terminal card: an error sentence with a still-enabled action
   * button under it offers a thing that can only refuse again. */
  onGone: () => void;
}

function RecordGrantPortalView({ token, view, onDone, onGone, brand }: RecordGrantPortalViewProps) {
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const result = await submitRecordGrantAction(token, file ?? undefined, file?.name);
      if (result.outcome === 'actioned') {
        onDone(result.action);
      } else if (result.outcome === 'consumed' || result.outcome === 'invalid') {
        // Terminal, not an error above a live button — the link is spent and
        // no second press can change that.
        onGone();
      } else {
        // 'conflict' — the state moved under this press; the same press can
        // legitimately be tried again.
        setError('This changed while you were looking at it — try once more.');
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Could not reach the server — nothing was sent. Please try again.'
      );
    } finally {
      setBusy(false);
    }
  }

  if (view.kind === 'proposal_approval') {
    if (view.alreadyActioned) {
      return (
        <PortalFrame brand={brand} footNote="A private link. No account needed.">
          <div class="portal__lead">Already decided.</div>
          <div class="portal__soft">This was already actioned — nothing more to do here.</div>
        </PortalFrame>
      );
    }
    return (
      <PortalFrame brand={brand} footNote="A private link. No account needed.">
        <div class="portal__lead">A request for your approval</div>
        {view.proposal.rationale && <div class="portal__soft">{view.proposal.rationale}</div>}
        {error && <div class="record-grant-portal__error">{error}</div>}
        <button type="button" class="record-grant-portal__action" disabled={busy} onClick={() => void submit()}>
          {busy ? 'Sending…' : 'Approve'}
        </button>
      </PortalFrame>
    );
  }

  // kind === 'record_view'
  if (view.alreadyActioned) {
    return (
      <PortalFrame brand={brand} footNote="A private link. No account needed.">
        <div class="portal__lead">Already actioned.</div>
        <div class="portal__soft">Nothing more to do here.</div>
      </PortalFrame>
    );
  }

  return (
    <PortalFrame brand={brand} footNote="A private link. No account needed.">
      <div class="portal__lead">{view.schema.label}</div>
      <FieldValueList schema={view.schema} record={toRecordRow(view.record)} />
      {view.children.length > 0 && (
        <ul class="record-grant-portal__children">
          {view.children.map((c) => (
            <li key={c.id} class="record-grant-portal__child">
              {/* A raw type key (`aa_shoot_day`) is vocabulary an outsider
                  was never taught. The payload carries one schema — the main
                  record's; a child of another type gets the plain generic. */}
              <span class="record-grant-portal__child-type">
                {c.recordType === view.record.recordType ? view.schema.label : 'a record'}
              </span>
              <span class="record-grant-portal__child-name">{c.displayName}</span>
            </li>
          ))}
        </ul>
      )}

      {error && <div class="record-grant-portal__error">{error}</div>}

      {view.allowedAction === 'upload' && (
        <input
          type="file"
          class="record-grant-portal__file"
          onChange={(e) => setFile((e.target as HTMLInputElement).files?.[0] ?? null)}
        />
      )}
      {view.allowedAction !== 'view' && (
        <button
          type="button"
          class="record-grant-portal__action"
          disabled={busy || (view.allowedAction === 'upload' && !file)}
          onClick={() => void submit()}
        >
          {busy ? 'Sending…' : ACTION_LABEL[view.allowedAction]}
        </button>
      )}
    </PortalFrame>
  );
}
